import { useDispatch, useSelector } from "react-redux";
import authModule from "../../../../../../utilities/AuthModule";
import { IFilePreCommitResult } from "../../../../interfaces/IFilePreCommitResult";
import { IFileToUpload } from "../../../../interfaces/IFileToUpload";
import { ISiteFileDestinationInfo } from "../../../../interfaces/ISiteFileDestinationInfo";
import { setFiles } from "../../../../redux/modules/file";
import { RootState } from "../storeMain";
import { FolderPreCommitRequest } from "../api/local/api-local-interfaces";
import { apiFilePreCommitCheck, apiFolderPreCommitCheck } from "../../../../services/ApiService";
import { SaveMode } from "../../../enums/SaveModeEnum";
import { setIsCheckingForDuplicates } from "../../../../redux/modules/savingInBackground";
import { FolderView } from "../../../helpers/folderView";
import { logger } from "../../../../../../utilities/logger";
import { resolveDestinationUrl } from "../../../helpers/urlDestination";
import { willSaveAsPdf } from "../../../helpers/stringHelper";
import { useSaveFormat } from "./useSaveFormat";

export function useFiles() {
  const dispatch = useDispatch();

  // Effective convertEmailToPDF for the chosen save format (per-save pick or the
  // persisted default) — not the raw user setting, which the pick can override.
  const { convertEmailToPDF } = useSaveFormat();
  const tenantReplaceFileNameWithGUID = useSelector((state: RootState) => state.tenantInfo?.replaceFileNameWithGUID);
  const siteInfo = useSelector((state: RootState) => state.tenantInfo?.sites);
  const currentDirectory = useSelector((state: RootState) => state.workingDirectories?.workingDirectories.length > 0 ? state.workingDirectories.workingDirectories[state.workingDirectories.workingDirectories.length - 1] : null);
  
  const checkPdfFiles = async (saveMode: SaveMode, files: IFileToUpload[]) => {
    let fileCheckResults: IFileToUpload[];
    if (!tenantReplaceFileNameWithGUID) {
      dispatch(setIsCheckingForDuplicates(true));
      try {

        const destUrl = await resolveDestinationUrl(currentDirectory);

        fileCheckResults = await getFilePreCommitCheckAsync(files, {
          autoFolderCreation: !(new FolderView(siteInfo, currentDirectory).canViewFolder()),
          destinationUrl: destUrl,
          siteUrl: currentDirectory.webUrl
        }, false, saveMode);

      } catch (error) {
        logger(error, "useFiles.ts (checkPdfFiles)");
      }
      dispatch(setIsCheckingForDuplicates(false));
    }
    return fileCheckResults;
  }

  const getFilePreCommitCheckAsync = async (
    files: IFileToUpload[],
    siteFileDestinationInfo: ISiteFileDestinationInfo,
    isOneDrive: boolean,
    saveMode?: SaveMode
  ): Promise<IFileToUpload[]> => {
    const accessToken = !isOneDrive
      ? await authModule.getSharePointAccessToken()
      : await authModule.getOneDriveAccessToken();

    const results = await Promise.all(
      files.map(async (item) => {
        try {
          let result: IFilePreCommitResult;

          if (item.isSelected) {
            //root folder
            if (item.isFolder && !item.parentFolderId) {
              const folderRequest: FolderPreCommitRequest = {
                accessToken: isOneDrive ? await authModule.getOneDriveAccessToken() : await authModule.getSharePointAccessToken(),
                destinationUrl: siteFileDestinationInfo.destinationUrl,
                siteUrl: siteFileDestinationInfo.siteUrl,
                folderName: item.name
              }

              result = await apiFolderPreCommitCheck(folderRequest);
            }
            //root file
            else if (!item.isFolder && !item.parentFolderId) {
              result = await apiFilePreCommitCheck({
                accessToken,
                autoFolderCreation: siteFileDestinationInfo.autoFolderCreation,
                fileName: willSaveAsPdf(item, convertEmailToPDF, saveMode) ? `${item.name}.pdf` : `${item.name}.${item.extension}`,
                destinationUrl: siteFileDestinationInfo.destinationUrl,
                siteUrl: siteFileDestinationInfo.siteUrl,
              })
            } else {
              result = { exists: false, checkedOut: false, checkedOutBy: null, locked: false, lockedBy: null }
            }
          } else {
            result = { exists: false, checkedOut: false, checkedOutBy: null, locked: false, lockedBy: null }
          }

          return {
            ...item,
            isDuplicate: result.exists,
            filePreCommitResult: result,
          };

        } catch (error) {
          const result: IFilePreCommitResult = {
            exists: false,
            checkedOut: false,
            checkedOutBy: "",
            locked: false,
            lockedBy: ""
          };

          return {
            ...item,
            isDuplicate: result.exists,
            filePreCommitResult: result,
          };
        }
      })
    );

    if (results) {
      dispatch(setFiles(results));
    }

    return results;
  };

  return {
    getFilePreCommitCheckAsync,
    checkPdfFiles
  }
}
